/**
 * ConstraintEditor — the hard filters a candidate cell must pass. Each row is a
 * metric (from the catalogue) + one of four shapes:
 *   - comparison   op (>=, <=, ==, >, <) + value + unit
 *   - allow-list   a set of accepted categorical values (e.g. 18650, 21700)
 *   - range        min / max + unit
 *   - exclude list a set of rejected categorical values (v2 `{not_in: [...]}`,
 *                  e.g. a manufacturer blocklist)
 * Order is irrelevant (they're ANDed), so no reorder — just add / remove.
 */
import { RowControls } from './RowControls.jsx'
import { MetricPicker } from './MetricPicker.jsx'
import { FlowHelp } from './FlowHelp.jsx'

const OPS = ['>=', '<=', '==', '>', '<']

function emptyConstraintRow(id) {
  return { id, metric: '', kind: 'cmp', op: '>=', value: '', unit: '', values: [], min: '', max: '' }
}

let _seq = 0

export function ConstraintEditor({ rows, onChange, metrics, readOnly = false }) {
  // See WeightsEditor: `readOnly` marks the fields an imported MATLAB script
  // owns. FlowEditor renders the provenance line and the deliberate unlock.
  const update = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  return (
    <section className="admin-aim-section admin-flow-section">
      <div className="admin-flow-section__head">
        <h3 className="admin-aim-section-title">
          Hard constraints <FlowHelp k="hardConstraints" />
        </h3>
        {!readOnly && (
          <button
            type="button"
            className="admin-ai-btn admin-ai-btn--ghost admin-flow-add"
            onClick={() => onChange([...rows, emptyConstraintRow(`c${++_seq}`)])}
          >
            + Add constraint
          </button>
        )}
      </div>
      <p className="admin-flow-hint">
        The flow's suggested default gates. Ion proposes each to the customer
        and only excludes cells once the customer confirms it — a suggested gate
        never filters on its own. Keep them few and sensible.
      </p>
      {rows.length === 0 && (
        <p className="admin-aim-empty admin-flow-empty">
          No hard filters — every candidate qualifies.
        </p>
      )}
      {/* One `fieldset[disabled]` instead of a `disabled` prop on each of the
          dozen inputs a constraint row can render: the browser disables every
          descendant control, including the remove buttons, so a new input added
          later cannot quietly stay editable. */}
      <fieldset className="admin-flow-fieldset" disabled={readOnly}>
      <ul className="admin-flow-list" role="list">
        {rows.map((r, i) => (
          <li key={r.id} className="admin-flow-constraint-row">
            <MetricPicker
              value={r.metric}
              metrics={metrics}
              ariaLabel={`Constraint ${i + 1} metric`}
              onChange={(metric) => update(i, { metric })}
            />
            <select
              className="admin-aim-input admin-flow-kind"
              value={r.kind}
              aria-label={`Constraint ${i + 1} type`}
              onChange={(e) => update(i, { kind: e.currentTarget.value })}
            >
              <option value="cmp">comparison</option>
              <option value="list">allow-list</option>
              <option value="range">range</option>
              <option value="exclude">exclude list</option>
            </select>

            {r.kind === 'cmp' && (
              <>
                <select
                  className="admin-aim-input admin-flow-op"
                  value={r.op}
                  aria-label={`Constraint ${i + 1} operator`}
                  onChange={(e) => update(i, { op: e.currentTarget.value })}
                >
                  {OPS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
                <input
                  className="admin-aim-input admin-flow-num"
                  type="number"
                  step="any"
                  placeholder="value"
                  value={r.value}
                  aria-label={`Constraint ${i + 1} value`}
                  onInput={(e) => update(i, { value: e.currentTarget.value })}
                />
                <input
                  className="admin-aim-input admin-flow-unit"
                  type="text"
                  placeholder="unit"
                  value={r.unit}
                  aria-label={`Constraint ${i + 1} unit`}
                  onInput={(e) => update(i, { unit: e.currentTarget.value })}
                />
              </>
            )}

            {r.kind === 'list' && (
              <input
                className="admin-aim-input admin-flow-list-vals"
                type="text"
                placeholder="accepted values, comma-separated (e.g. 18650, 21700)"
                value={(r.values || []).join(', ')}
                aria-label={`Constraint ${i + 1} allowed values`}
                onInput={(e) =>
                  update(i, {
                    values: e.currentTarget.value.split(',').map((v) => v.trim()),
                  })
                }
              />
            )}

            {r.kind === 'exclude' && (
              <input
                className="admin-aim-input admin-flow-list-vals"
                type="text"
                placeholder="excluded values, comma-separated (e.g. acme, bad-corp)"
                value={(r.values || []).join(', ')}
                aria-label={`Constraint ${i + 1} excluded values`}
                onInput={(e) =>
                  update(i, {
                    values: e.currentTarget.value.split(',').map((v) => v.trim()),
                  })
                }
              />
            )}

            {r.kind === 'range' && (
              <>
                <input
                  className="admin-aim-input admin-flow-num"
                  type="number"
                  step="any"
                  placeholder="min"
                  value={r.min}
                  aria-label={`Constraint ${i + 1} min`}
                  onInput={(e) => update(i, { min: e.currentTarget.value })}
                />
                <input
                  className="admin-aim-input admin-flow-num"
                  type="number"
                  step="any"
                  placeholder="max"
                  value={r.max}
                  aria-label={`Constraint ${i + 1} max`}
                  onInput={(e) => update(i, { max: e.currentTarget.value })}
                />
                <input
                  className="admin-aim-input admin-flow-unit"
                  type="text"
                  placeholder="unit"
                  value={r.unit}
                  aria-label={`Constraint ${i + 1} unit`}
                  onInput={(e) => update(i, { unit: e.currentTarget.value })}
                />
              </>
            )}

            <RowControls
              index={i}
              count={rows.length}
              label={`constraint ${i + 1}`}
              onRemove={() => onChange(rows.filter((_, idx) => idx !== i))}
            />
          </li>
        ))}
      </ul>
      </fieldset>
    </section>
  )
}
